/**
 * Domains Page
 *
 * Lists all domains (subdomains and custom) with ability to add custom domains.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Check, AlertCircle, Globe } from 'lucide-react';
import { useAppCache } from '@/contexts/AppCacheContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { FloatingInput } from '@/components/ui/floating-input';
import { LoadingButton } from '@/components/ui/loading-button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDomains, useCreateDomain, useVerifyDomain, useDeleteDomain } from '@/hooks/useDomains';
import { Paginator } from '@/components/ui/paginator';
import { Domain, DnsInstructions, domainsApi, isApiClientError } from '@/api/domainsApi';
import { useAuth } from '../auth/auth0-provider';
import { normalizeDomain } from '@/utils/domain';
import { Time } from '@/components/ui/time';
import {
  DnsVerificationStep,
  DnsPropagationNote,
  DomainAddedSuccess,
  OwnershipVerifiedSuccess,
  getDomainStatus,
  DomainStatusBadge,
  DomainTypeBadge,
  DomainCell,
  DomainActionsDropdown,
} from '@/components/domains';

/**
 * Page Header Component - eliminates duplication
 */
interface PageHeaderProps {
  onAddDomain: () => void;
  canCreate: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({ onAddDomain, canCreate }) => (
  <div className="mb-8 flex items-center justify-between">
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Domains</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Manage all of the domains connected to your sites.
      </p>
    </div>
    {canCreate && (
      <Button variant="primary" size="default" onClick={onAddDomain}>
        <Plus className="h-4 w-4 stroke-[1.5]" />
        Add custom domain
      </Button>
    )}
  </div>
);

/**
 * Add Domain Modal
 */
interface AddDomainModalProps {
  open: boolean;
  onClose: () => void;
}

const AddDomainModal: React.FC<AddDomainModalProps> = ({ open, onClose }) => {
  const [rawInput, setRawInput] = useState('');
  const [dnsInstructions, setDnsInstructions] = useState<DnsInstructions | null>(null);
  const [normalizedDomain, setNormalizedDomain] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Becomes true when the user clicks Submit with an invalid input. Drives the
  // inline validation message; cleared on the next keystroke so the message
  // disappears as soon as they're correcting it.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const createDomain = useCreateDomain();
  const isCreating = createDomain.isPending;
  const error = submitError;

  const validation = rawInput ? normalizeDomain(rawInput) : { domain: '', isValid: false };
  const hasValidDomain = validation.isValid;
  const showError = submitAttempted && !hasValidDomain;
  const showLiveSuccess = rawInput.length > 0 && hasValidDomain;
  const showHelper = rawInput.length === 0 && !submitAttempted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasValidDomain) {
      setSubmitAttempted(true);
      return;
    }
    setSubmitAttempted(false);
    setSubmitError(null);
    try {
      const result = await createDomain.mutateAsync({ domain: validation.domain });
      setNormalizedDomain(validation.domain);
      setDnsInstructions(result.dnsInstructions ?? null);
    } catch (err) {
      const message = isApiClientError(err)
        ? err.message
        : err instanceof Error
        ? err.message
        : 'Failed to create domain';
      setSubmitError(message);
    }
  };

  const handleClose = () => {
    setRawInput('');
    setNormalizedDomain('');
    setDnsInstructions(null);
    setSubmitError(null);
    setSubmitAttempted(false);
    onClose();
  };

  const txtRecord = dnsInstructions?.verification?.record;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add custom domain</DialogTitle>
          <DialogDescription>
            {!dnsInstructions
              ? 'Add your domain now and verify ownership. You can attach it to any site later.'
              : <>Add this TXT record to verify ownership of <span className="font-mono font-medium">{normalizedDomain}</span></>
            }
          </DialogDescription>
        </DialogHeader>

        {!dnsInstructions ? (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <FloatingInput
                  type="text"
                  label="Domain"
                  prefix="https://"
                  value={rawInput}
                  onChange={(e) => {
                    setRawInput(e.target.value);
                    if (submitAttempted) setSubmitAttempted(false);
                  }}
                  state={
                    showError ? 'error' : showLiveSuccess ? 'success' : 'default'
                  }
                />
                {showLiveSuccess && validation.domain !== rawInput && (
                  <p className="text-sm text-success mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3 stroke-[1.5]" />
                    Will be saved as: <span className="font-mono">{validation.domain}</span>
                  </p>
                )}
                {showLiveSuccess && validation.domain === rawInput && (
                  <p className="text-sm text-success mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3 stroke-[1.5]" />
                    Valid domain format
                  </p>
                )}
                {showError && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 stroke-[1.5]" />
                    {validation.error || 'Enter a domain to continue'}
                  </p>
                )}
                {showHelper && (
                  <p className="text-sm text-muted-foreground mt-1">
                    We'll automatically clean up http://, www., and trailing paths
                  </p>
                )}
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button size="default" type="button" variant="tertiary" onClick={handleClose}>
                Cancel
              </Button>
              <Button size="default" type="submit" variant="primary" disabled={isCreating}>
                {isCreating ? 'Adding...' : 'Add domain'}
              </Button>
            </DialogFooter>
          </form>
        ) : txtRecord ? (
          <div className="space-y-4 py-2">
            <DomainAddedSuccess />

            <DnsVerificationStep
              record={txtRecord}
              stepNumber={1}
            />

            <DnsPropagationNote />

            <DialogFooter>
              <Button size="dense" variant="primary" onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Failed to load DNS instructions
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * DNS Instructions Modal for existing domains
 */
interface DnsInstructionsModalProps {
  open: boolean;
  onClose: () => void;
  domain: string;
  instructions: DnsInstructions | null;
  isLoading: boolean;
  onVerify: () => void;
  isVerifying: boolean;
  verificationResult: 'success' | 'failed' | null;
}

const DnsInstructionsModal: React.FC<DnsInstructionsModalProps> = ({
  open,
  onClose,
  domain,
  instructions,
  isLoading,
  onVerify,
  isVerifying,
  verificationResult,
}) => {
  const isVerified = verificationResult === 'success';
  const txtRecord = instructions?.verification?.record;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Verify domain ownership</DialogTitle>
          <DialogDescription>
            Add this TXT record to verify ownership of <span className="font-mono font-medium">{domain}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="">
          {isLoading ? (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-foreground/5">
                <div className="flex items-center gap-2 mb-3">
                  <Skeleton className="h-6 w-6 rounded-full bg-foreground/5" />
                  <Skeleton className="h-4 w-32 bg-foreground/5" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Skeleton className="h-3 w-16 mb-2 bg-foreground/5" />
                    <Skeleton className="h-10 w-full bg-foreground/5" />
                  </div>
                  <div>
                    <Skeleton className="h-3 w-16 mb-2 bg-foreground/5" />
                    <Skeleton className="h-10 w-full bg-foreground/5" />
                  </div>
                </div>
              </div>
            </div>
          ) : txtRecord ? (
            <div className="space-y-4">
              <DnsVerificationStep
                record={txtRecord}
                stepNumber={1}
                error={verificationResult === 'failed' ? 'TXT record not found. Please check your DNS configuration.' : null}
              />

              {isVerified && <OwnershipVerifiedSuccess />}

              <DnsPropagationNote />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Failed to load DNS instructions
            </div>
          )}
        </div>

        <DialogFooter>
          <Button size="dense" variant="tertiary" onClick={onClose}>Cancel</Button>
          <LoadingButton size="dense" variant="primary" onClick={onVerify} loading={isVerifying} loadingText="Verifying...">
            <RefreshCw className="h-4 w-4 mr-2 stroke-[1.5]" />
            Check verification
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main Domains Page
 */
const Domains: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [verifyingDomainId, setVerifyingDomainId] = useState<string | null>(null);
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [viewingDnsForDomain, setViewingDnsForDomain] = useState<Domain | null>(null);
  const [dnsInstructions, setDnsInstructions] = useState<DnsInstructions | null>(null);
  const [isLoadingDns, setIsLoadingDns] = useState(false);
  const [verificationResults, setVerificationResults] = useState<Record<string, 'success' | 'failed' | null>>({});
  const [modalVerificationResult, setModalVerificationResult] = useState<'success' | 'failed' | null>(null);

  const [page, setPage] = useState(1);
  const { data: domainsData, isLoading: queryLoading, error: queryError, refetch: queryRefetch } = useDomains({ page });
  const { isAuthenticated, isSdkReady } = useAuth();
  const domains = domainsData?.items ?? [];
  const pagination = domainsData?.pagination;
  // Mirror the cold-load pattern from Task 2: treat the auth-init window as
  // loading so the empty-state doesn't flash before the query is enabled.
  const isLoading = queryLoading || !isAuthenticated || !isSdkReady;
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : String(queryError)
    : null;
  const refetch = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);
  const { hasPermission } = useAppCache();
  const confirm = useConfirm();
  const toast = useToast();

  const canCreate = hasPermission('create:domains');

  // Open modal if action=add query param is present (only on mount)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'add') {
      setIsAddModalOpen(true);
      params.delete('action');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
    }
  }, []);

  // Clamp the page when data arrives (e.g. after deleting the last domain on
  // the final page) so the user isn't stranded on an empty page.
  useEffect(() => {
    if (pagination && page > pagination.totalPages) {
      setPage(Math.max(1, pagination.totalPages));
    }
  }, [pagination, page]);

  const verifyDomainMutation = useVerifyDomain();
  const deleteDomainMutation = useDeleteDomain();

  const handleVerify = async (domainId: string) => {
    const domainRow = domains.find((d) => d.id === domainId);
    const domainLabel = domainRow?.domain ?? 'domain';

    setVerifyingDomainId(domainId);
    setVerificationResults(prev => ({ ...prev, [domainId]: null }));

    try {
      const result = await verifyDomainMutation.mutateAsync(domainId);
      const isVerified = result?.ownership?.verified || result?.fullyVerified;

      setVerificationResults(prev => ({ ...prev, [domainId]: isVerified ? 'success' : 'failed' }));

      if (isVerified) {
        toast.success(`${domainLabel} is verified.`, 'Domain verified');
      } else {
        toast.error(`We couldn't find the TXT record for ${domainLabel}. DNS may still be propagating.`, 'Verification failed');
      }

      setTimeout(() => {
        setVerificationResults(prev => ({ ...prev, [domainId]: null }));
      }, 2400);

      setVerifyingDomainId(null);
      // onSuccess of the mutation invalidates ['domains']; no manual refetch needed.
      return result;
    } catch (err) {
      setVerificationResults(prev => ({ ...prev, [domainId]: 'failed' }));
      const message = err instanceof Error ? err.message : 'Something went wrong while checking this domain.';
      toast.error(message, 'Verification failed');
      setTimeout(() => {
        setVerificationResults(prev => ({ ...prev, [domainId]: null }));
      }, 2400);
      setVerifyingDomainId(null);
      return null;
    }
  };

  const handleShowDnsInstructions = async (domain: Domain) => {
    setViewingDnsForDomain(domain);
    setDnsInstructions(null);
    setModalVerificationResult(null);
    setIsLoadingDns(true);

    try {
      const result = await domainsApi.getDnsInstructions(domain.id);
      setDnsInstructions({
        verification: result.ownership,
        routing: result.routing ?? undefined,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load DNS instructions';
      toast.error(message, 'Failed to load DNS records');
    }
    setIsLoadingDns(false);
  };

  const handleVerifyFromModal = async () => {
    if (!viewingDnsForDomain) return;
    const domainLabel = viewingDnsForDomain.domain;
    setVerifyingDomainId(viewingDnsForDomain.id);

    try {
      const result = await verifyDomainMutation.mutateAsync(viewingDnsForDomain.id);
      const isVerified = result?.ownership?.verified || result?.fullyVerified;
      setModalVerificationResult(isVerified ? 'success' : 'failed');
      setVerifyingDomainId(null);
      // mutation onSuccess invalidates ['domains']
      if (isVerified) {
        toast.success(`${domainLabel} is verified.`, 'Domain verified');
        // Brief beat so the inline "Ownership verified" confirmation is
        // legible, then close the modal and return to the list.
        setTimeout(() => {
          setViewingDnsForDomain(null);
          setDnsInstructions(null);
          setModalVerificationResult(null);
        }, 900);
      }
    } catch {
      setModalVerificationResult('failed');
      setVerifyingDomainId(null);
    }
  };

  const handleDelete = async (domainId: string) => {
    const confirmed = await confirm({
      title: 'Remove domain',
      description: 'Are you sure you want to remove this domain?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setDeletingDomainId(domainId);
    try {
      await deleteDomainMutation.mutateAsync(domainId);
      toast.success('Domain removed.', 'Removed');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete domain';
      toast.error(message, 'Delete failed');
    }
    setDeletingDomainId(null);
    // mutation onSuccess invalidates ['domains']
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-6">
        <PageHeader onAddDomain={() => setIsAddModalOpen(true)} canCreate={canCreate} />

        {error ? (
          <EmptyState
            icon={AlertCircle}
            title="Couldn't load domains"
            description={error}
            action={{ label: 'Try again', onClick: () => refetch(), variant: 'tertiary' }}
          />
        ) : !isLoading && domains.length === 0 ? (
          <EmptyState
            icon={Globe}
            title="No domains yet"
            description="Domains will appear here when you create sites or add custom domains."
            action={canCreate ? {
              label: 'Add custom domain',
              onClick: () => setIsAddModalOpen(true),
            } : undefined}
          />
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[170px]">Status</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead className="w-[180px]">Site</TableHead>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead className="w-[140px]">Added</TableHead>
                  <TableHead className="w-[60px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell />
                    </TableRow>
                  ))
                ) : (
                  domains.map((domain) => (
                    <TableRow
                      key={domain.id}
                      className={
                        verificationResults[domain.id] === 'success'
                          ? 'animate-row-flash-success'
                          : verificationResults[domain.id] === 'failed'
                          ? 'animate-row-flash-error'
                          : ''
                      }
                    >
                      <TableCell>
                        <DomainStatusBadge status={getDomainStatus(domain)} />
                      </TableCell>
                      <TableCell>
                        <DomainCell
                          domain={domain.domain}
                          type={domain.type}
                        />
                      </TableCell>
                      <TableCell>
                        {domain.siteId ? (
                          <Link
                            to={`/sites/${domain.siteId}`}
                            className="text-foreground hover:underline"
                          >
                            {domain.siteName || domain.siteId}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DomainTypeBadge type={domain.type} />
                      </TableCell>
                      <TableCell>
                        <Time value={domain.createdAt} format="short" />
                      </TableCell>
                      <TableCell className="text-right">
                        <DomainActionsDropdown
                          domain={domain}
                          onShowRecords={() => handleShowDnsInstructions(domain)}
                          onVerify={() => handleVerify(domain.id)}
                          onDelete={() => handleDelete(domain.id)}
                          isVerifying={verifyingDomainId === domain.id}
                          isDeleting={deletingDomainId === domain.id}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <Paginator
              pagination={pagination}
              onPageChange={setPage}
              className="mt-6"
            />
          </div>
        )}
      </div>

      <AddDomainModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      <DnsInstructionsModal
        open={viewingDnsForDomain !== null}
        onClose={() => {
          setViewingDnsForDomain(null);
          setDnsInstructions(null);
          setModalVerificationResult(null);
        }}
        domain={viewingDnsForDomain?.domain || ''}
        instructions={dnsInstructions}
        isLoading={isLoadingDns}
        onVerify={handleVerifyFromModal}
        isVerifying={verifyingDomainId === viewingDnsForDomain?.id}
        verificationResult={modalVerificationResult}
      />
    </div>
  );
};

export default Domains;
